import { authFetch } from '../../../api/csrf';
import React, { useState, useEffect } from 'react';
import { useSearchParams, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
    LayoutDashboard,
    PlayCircle,
    FileText,
} from 'lucide-react';

import ModuleLayout from '../../../layouts/ModuleLayout';
import Drawer from '../../../components/common/Drawer';
import PermissionGate from '../../../components/common/PermissionGate/PermissionGate';
import { payslipsApi } from '../../../api/payslipsApi';

import PayrollDashboard from './PayrollDashboard';
import PayrollRun from './PayrollRun';
import PayslipManagement from './PayslipManagement';
import PayslipBatchDetails from './PayslipBatchDetails';
import PayslipEmployeeView from './PayslipEmployeeView';
import PayslipDetailView from '../../../components/payroll/PayslipDetailView';

const AccessDenied = () => (
    <div className="flex flex-col items-center justify-center p-12 text-center text-gray-500 bg-gray-50 rounded-lg">
        <h3 className="text-lg font-medium text-gray-900 mb-2">Access Denied</h3>
        <p>You do not have permission to view this section.</p>
    </div>
);

const PayrollModule = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();
    const navigate = useNavigate();
    const params = useParams();

    const tabs = [
        { id: 'payrollDashboard', label: 'Payroll Dashboard', icon: LayoutDashboard },
        { id: 'payrollRun', label: 'Payroll Runs', icon: PlayCircle },
        { id: 'payslips', label: 'Payslips', icon: FileText },
    ];

    const getTabFromPath = () => {
        const path = location.pathname;
        if (path.includes('/payroll_dashboard') || path === '/payroll' || path === '/hrms/payroll') return 'payrollDashboard';
        if (path.includes('/payroll_run')) return 'payrollRun';
        if (path.includes('/payslips')) return 'payslips';
        return 'payrollDashboard';
    };

    const initialTab = searchParams.get('tab') || getTabFromPath();
    const [activeTab, setActiveTab] = useState(initialTab);

    useEffect(() => {
        const currentTabIcon = searchParams.get('tab');
        if (currentTabIcon && currentTabIcon !== activeTab) {
            setActiveTab(currentTabIcon);
        } else if (!currentTabIcon) {
            const pathTab = getTabFromPath();
            if (pathTab !== activeTab) {
                setActiveTab(pathTab);
            }
        }
    }, [searchParams, location.pathname]);

    const handleTabChange = (tabId) => {
        setActiveTab(tabId);

        const tabRoutes = {
            'payrollDashboard': '/payroll_dashboard',
            'payrollRun': '/payroll_run',
            'payslips': '/payslips'
        };

        const targetPath = tabRoutes[tabId] || '/payroll_dashboard';
        navigate(targetPath);
    };

    const action = searchParams.get('action');
    const actionId = searchParams.get('id') || params.batchId;

    const closeDrawer = () => {
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('action');
        newParams.delete('id');

        navigate({ pathname: location.pathname, search: newParams.toString() });
    };

    const handleViewPayslip = (id) => {
        setSearchParams({ tab: 'payslips', action: 'viewPayslip', id });
    };

    const renderTabContent = () => {
        switch (activeTab) {
            case 'payrollDashboard':
                return <PayrollDashboard />;
            case 'payrollRun':
                return (
                    <PermissionGate permission="payroll" action="read" showFallback fallback={<AccessDenied />}>
                        <PayrollRun />
                    </PermissionGate>
                );
            case 'payslips':
                return (
                    <PermissionGate permission="payroll" action="read" showFallback fallback={<AccessDenied />}>
                        <PayslipManagement onViewPayslip={handleViewPayslip} />
                    </PermissionGate>
                );

            default:
                return <PayrollDashboard />;
        }
    };

    const isBatchRoute = location.pathname.includes('/payslips/batch/');
    const isPayslipDetailRoute = /\/payslips\/batch\/[^/]+\/payslip\//.test(location.pathname);

    return (
        <ModuleLayout
            title="Payroll Management"
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={handleTabChange}
        >
            {!isBatchRoute && renderTabContent()}

            {isBatchRoute && !isPayslipDetailRoute && (
                <div className="p-6">
                    <PayslipBatchDetails />
                </div>
            )}

            {isPayslipDetailRoute && (
                <div className="p-6">
                    <PayslipEmployeeView />
                </div>
            )}

            <Drawer
                isOpen={action === 'viewPayslip'}
                onClose={closeDrawer}
                title="Payslip Details"
                size="medium"
            >
                <PayslipFetcher id={actionId} onClose={closeDrawer} />
            </Drawer>

        </ModuleLayout>
    );
};

const PayslipFetcher = ({ id, onClose }) => {
    const [payslip, setPayslip] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!id) return;
        let isMounted = true;

        const fetch = async () => {
            try {
                setLoading(true);
                const result = await payslipsApi.getPayslipById(id);
                const payload =
                    result?.responseCode === 200 && result?.data
                        ? result.data
                        : result?.data || result;
                if (isMounted) {
                    setPayslip(payload);
                    setLoading(false);
                    setError(null);
                }
            } catch (e) {
                if (isMounted) {
                    setError("Failed to load payslip.");
                    setLoading(false);
                }
            }
        };
        authFetch();
        return () => { isMounted = false; };
    }, [id]);

    if (loading) return <div className="p-6 flex justify-center text-gray-500">Loading payslip...</div>;
    if (error) return <div className="p-6 text-red-500 text-center">{error}</div>;

    return <PayslipDetailView payslip={payslip} />;
};

export default PayrollModule;
